import React from 'react';

export function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

type Tone = 'slate' | 'blue' | 'green' | 'amber' | 'red' | 'violet';

const toneClasses: Record<Tone, string> = {
  slate: 'admin-tone-slate',
  blue: 'admin-tone-blue',
  green: 'admin-tone-green',
  amber: 'admin-tone-amber',
  red: 'admin-tone-red',
  violet: 'admin-tone-violet',
};

export function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="admin-page">
      <div className="admin-page-content">{children}</div>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  meta,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  meta?: React.ReactNode;
}) {
  return (
    <div className="admin-page-header">
      <div>
        <h1 className="admin-page-title">{title}</h1>
        {description && <p className="admin-page-description">{description}</p>}
        {meta && <div className="admin-page-meta">{meta}</div>}
      </div>
      {actions && <div className="admin-page-header-actions">{actions}</div>}
    </div>
  );
}

export function FilterBar({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('admin-panel admin-filter-bar', className)}>
      {children}
    </div>
  );
}

export function StatusBadge({
  children,
  tone = 'slate',
  className,
}: {
  children: React.ReactNode;
  tone?: Tone;
  className?: string;
}) {
  return (
    <span className={cx('admin-status', toneClasses[tone], className)}>
      {children}
    </span>
  );
}

export function MetricCard({ label, value, tone = 'slate' }: { label: string; value: React.ReactNode; tone?: Tone }) {
  return (
    <div className={cx('admin-metric', toneClasses[tone])}>
      <div className="admin-metric-label">{label}</div>
      <div className="admin-metric-value">{value}</div>
    </div>
  );
}

export function InfoTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="admin-info-tile">
      <div className="admin-info-label">{label}</div>
      <div className="admin-info-value">{value}</div>
    </div>
  );
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-8 text-center text-gray-500">{children}</td>
    </tr>
  );
}

export function PaginationBar({
  loading,
  pageNumber,
  pageSize,
  displayPage,
  rowCount,
  totalElements,
  isLast,
  onPrevious,
  onNext,
  previousLabel,
  nextLabel,
}: {
  loading: boolean;
  pageNumber: number;
  pageSize: number;
  displayPage?: { number: number; size: number };
  rowCount: number;
  totalElements?: number;
  isLast?: boolean;
  onPrevious: () => void;
  onNext: () => void;
  previousLabel: string;
  nextLabel: string;
}) {
  // Retained rows use their response metadata, even if another page was requested.
  const offset = (displayPage?.number ?? pageNumber) * (displayPage?.size ?? pageSize);
  const start = offset + (rowCount ? 1 : 0);
  const end = offset + rowCount;
  return (
    <div className="admin-pagination">
      <button className="btn btn-neutral" disabled={loading || pageNumber <= 0} onClick={onPrevious}>{previousLabel}</button>
      <div className="admin-pagination-range">{totalElements == null ? '—' : `${start}–${end} / ${totalElements}`}</div>
      <button className="btn btn-neutral" disabled={loading || !!isLast} onClick={onNext}>{nextLabel}</button>
    </div>
  );
}

export function ActionMenu({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDetailsElement>(null);
  const triggerRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return <details ref={menuRef} open={open} className="admin-action-menu">
    <summary ref={triggerRef} role="button" aria-expanded={open} className="btn btn-neutral"
      onClick={(event) => { event.preventDefault(); setOpen((value) => !value); }}>
      {label}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
    </summary>
    <div className="admin-action-menu-items" onClick={(event) => {
      if (event.target instanceof Element && event.target.closest('button, a')) {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }}>{children}</div>
  </details>;
}

export function toneForRideStatus(status?: string | null): Tone {
  switch (status) {
    case 'COMPLETED':
      return 'green';
    case 'SCHEDULED':
      return 'blue';
    case 'STARTED':
      return 'amber';
    case 'CANCELLED':
      return 'red';
    case 'EXPIRED':
      return 'slate';
    default:
      return 'slate';
  }
}

export function toneForPaymentStatus(status?: string | null): Tone {
  switch (status) {
    case 'CAPTURED':
      return 'green';
    case 'AUTHORIZED':
    case 'CAPTURE_PENDING':
      return 'blue';
    case 'CAPTURE_FAILED':
      return 'red';
    case 'CANCELLED':
    case 'NOT_REQUIRED':
      return 'slate';
    default:
      return 'amber';
  }
}
